import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import AboutSideNav from '../../../../components/about/AboutSideNav';

// ===== History (EN mirror) — full rebuild, history scope only =====
// Design language mirrors JA history + EN overview (navy #1a2942 / brass #a38e65 / gold accent #d7b779 /
//   jba-display / editorial rules / BTN-08 / AboutSideNav / era-chaptered full-version timeline).
// Facts sourced from the same canon as the JA page (about/history working tree, about/overview,
//   mng-thebutler/00). No invented figures. All events are internal to the association — no claims of
//   involvement with real external entities. EN pages must contain no Japanese characters (SEO rule 1-3):
//   era badges use Roman numerals instead of kanji.

export default function EnAboutHistoryPage() {
  useEffect(() => {
    document.title = 'History & Heritage | Japan Butler Association';

    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute('content', 'The 135-year history of the Japan Butler Association, told through four eras — from its 1890 founding and nationwide growth to reunification and the present day of butler culture.');
    if (!document.querySelector('meta[name="description"]')) {
      document.head.appendChild(metaDescription);
    }

    const canonicalLink = document.querySelector('link[rel="canonical"]') || document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://butler-association.jp/en/about/history');
    if (!document.querySelector('link[rel="canonical"]')) {
      document.head.appendChild(canonicalLink);
    }

    const hreflangJa = document.querySelector('link[rel="alternate"][hreflang="ja"]') || document.createElement('link');
    hreflangJa.setAttribute('rel', 'alternate');
    hreflangJa.setAttribute('hreflang', 'ja');
    hreflangJa.setAttribute('href', 'https://butler-association.jp/about/history');
    if (!document.querySelector('link[rel="alternate"][hreflang="ja"]')) {
      document.head.appendChild(hreflangJa);
    }

    const hreflangEn = document.querySelector('link[rel="alternate"][hreflang="en"]') || document.createElement('link');
    hreflangEn.setAttribute('rel', 'alternate');
    hreflangEn.setAttribute('hreflang', 'en');
    hreflangEn.setAttribute('href', 'https://butler-association.jp/en/about/history');
    if (!document.querySelector('link[rel="alternate"][hreflang="en"]')) {
      document.head.appendChild(hreflangEn);
    }

    const hreflangDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]') || document.createElement('link');
    hreflangDefault.setAttribute('rel', 'alternate');
    hreflangDefault.setAttribute('hreflang', 'x-default');
    hreflangDefault.setAttribute('href', 'https://butler-association.jp/about/history');
    if (!document.querySelector('link[rel="alternate"][hreflang="x-default"]')) {
      document.head.appendChild(hreflangDefault);
    }

    document.documentElement.lang = 'en';
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en/' },
    { label: 'About Us', href: '/en/about' },
    { label: 'History' }
  ];

  const aboutNav = [
    { label: 'Overview', href: '/en/about/overview' },
    { label: 'Mission', href: '/en/about/mission' },
    { label: 'History', href: '/en/about/history' },
    { label: 'Organization', href: '/en/about/organization' },
    { label: 'Activities', href: '/en/about/activities' },
    { label: 'Access', href: '/en/about/access' }
  ];

  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  // Full-version era-chaptered timeline. All events internal to the association (no external claims).
  const eras = [
    {
      badge: 'I',
      name: 'The Founding',
      range: '1890 – 1923',
      image: '/images/generated/jba-fs-006-about_history.webp',
      imageAlt: 'A butler of the founding era, reflecting Western manners fused with Japanese etiquette in the association salon',
      narrative: [
        "The association's history begins amid the modernization that followed the Meiji Restoration. As Western culture flowed rapidly into the country, founder Shingo Tanaka brought back the conduct and spirit of the butler he had studied in Britain, resolved to establish authentic butler service in Japan.",
        'To bind Western discipline and dignity together with Japan\'s refined etiquette — that fusion was the ideal the association first raised. The founding years were devoted not to importing manners, but to shaping a distinctly Japanese idea of the butler, one attuned to daily life here.',
      ],
      rows: [
        { year: '1890', label: 'Foundation of the Association', text: 'Shingo Tanaka founded Japan\'s dedicated institution for butlers, raising the ideal of cultivating butlers who combine Western service with Japanese etiquette.' },
        { year: '1903', label: 'The First Training Program', text: 'The first systematic butler-training program opened, pairing classroom study with hands-on practice to establish a unique curriculum fusing Western service manners with Japanese etiquette.' },
        { year: '1923', label: 'A Code of Conduct', text: 'The association codified its own code of conduct and quality standards, systematizing the principles of etiquette, confidentiality, and bearing — the foundation for later education and certification.' },
      ],
    },
    {
      badge: 'II',
      name: 'Growth & Reach',
      range: '1935 – 1985',
      image: '/images/generated/jba-fs-007-about_history.webp',
      imageAlt: 'A training scene from the growth era, honing skills through a step-by-step curriculum',
      narrative: [
        'Upon the foundation laid in the founding years, the association deepened the very quality of its education. It progressively refined a training system that integrated Western service manners with Japanese etiquette, raising the standard of butler cultivation another degree.',
        'In time, activity spread beyond Tokyo to regions across the country. Providing service suited to each region\'s character while weaving a nationwide net of talent development — the growth era was the age in which the association matured from a point into a plane.',
      ],
      rows: [
        { year: '1935', label: 'Reorganized Training System', text: 'The training system integrating Western service manners with Japanese etiquette was fully reorganized, establishing a stepped curriculum of study and practice that raised the quality of cultivation.' },
        { year: '1955', label: 'Regional Associations', text: 'The Kanto and Kansai Butler Associations were established in succession, beginning region-specific service and the construction of a nationwide network.' },
        { year: '1970', label: 'Modernized Service', text: 'New training methods were introduced to modernize butler service, building a system of talent development that met the needs of the times while preserving tradition.' },
        { year: '1985', label: 'Nationwide Expansion', text: 'Service was expanded on a nationwide scale, with unified quality standards defined and the prototype of the later certification system introduced.' },
      ],
    },
    {
      badge: 'III',
      name: 'Reunion & Renewal',
      range: '2000 – 2020',
      image: '/images/generated/jba-fs-008-about_history.webp',
      imageAlt: 'Association butlers working together under unified nationwide quality standards after reunification',
      narrative: [
        'Over the course of its history, the association had once split into the Kanto Butler Association and the Kansai Butler Association. In 2000 the two became one again — under the leadership of second President Kazuhiko Yamada, reunification was achieved and a fresh start made as a nationwide unified organization.',
        'Reunification meant more than binding an organization together. By aligning quality standards and the education system as one across the country, the association could guarantee an unchanging standard of the butler in any region. The foundation of the present-day structure was shaped in this era.',
        'With the foundation set, the association broadened the depth of its service. More specialized offerings, a renewed education program, expansion to more regions — two decades of quiet renewal layered upon carefully guarded tradition.',
      ],
      rows: [
        { year: '2000', label: 'Reunification', text: 'The previously separate Kanto and Kansai associations reunified. Led by second President Kazuhiko Yamada, unified nationwide quality standards and an education system were established, setting the foundation of today\'s structure.' },
        { year: '2005', label: 'Expanded Specialist Service', text: 'More specialized service was developed to meet the requests of private occasions and corporate clients, advancing the cultivation of butlers able to respond to a wide range of settings.' },
        { year: '2008', label: 'Renewed Education Program', text: 'A new curriculum was introduced into the butler-training program, raising the quality of service further and enriching the certification system.' },
        { year: '2016', label: 'Reaching More Regions', text: 'Alongside expanded service in major cities, the association began earnest expansion to more regions, advancing the spread of butler culture suited to each area.' },
        { year: '2020', label: 'Contactless & Online Support', text: 'Contactless service and online support structures were put in place, building a system that responded flexibly to new ways of living while preserving cultivated manners.' },
      ],
    },
    {
      badge: 'IV',
      name: 'A New Era',
      range: '2023 – Present',
      image: '/images/generated/jba-fs-009-about_history-v2.webp',
      imageAlt: 'The butler of a new era, carrying tradition forward while attending to contemporary life',
      narrative: [
        'In 2023, Yuichi Ishii became the third President. While preserving the spirit of the butler handed down from founder Shingo Tanaka, he is advancing the "modernization of butler culture" through digitalization and international collaboration.',
        'Cultivating the next generation, strengthening how the craft is shared, responding to changes in daily life — bridging what has been inherited toward the next hundred years is the association\'s present mission. In 2025, the association marked its 135th anniversary.',
      ],
      rows: [
        { year: '2023', label: 'The Third President', text: 'Yuichi Ishii became the third President. Under new leadership, he aims to create the next generation of butler service through the fusion of tradition and innovation.' },
        { year: '2024', label: 'Next-Generation Program', text: 'A new program focused on cultivating young butlers began, combining honed skills with new methods of learning to raise the butlers who will carry the next era.' },
        { year: '2025', label: '135th Anniversary', text: 'The association marked its 135th anniversary and unfolded commemorative initiatives, reflecting on its journey while envisioning the era ahead.' },
      ],
    },
  ];

  // Presidents (lineage), aligned with the year system (First = founder / Second = 2000–2022 / Third = 2023–present).
  const presidents = [
    {
      gen: 'First President',
      name: 'Shingo Tanaka',
      period: '1890 · Founder',
      description: 'Amid the modernization following the Meiji Restoration, he introduced the butler culture he studied in Britain and founded the association, laying the cornerstone of a butler tradition fusing the West with the Japanese heart.',
    },
    {
      gen: 'Second President',
      name: 'Kazuhiko Yamada',
      period: '2000 – 2022',
      description: 'As second President, he led the reunification of the separated Kanto and Kansai associations, establishing unified nationwide quality standards and the education system that form the foundation of today\'s structure.',
    },
    {
      gen: 'Third President',
      name: 'Yuichi Ishii',
      period: '2023 – Present',
      description: 'As third President, he leads the association into a new era. While inheriting the traditional culture of the butler, he advances the "modernization of butler culture" through digitalization and international collaboration.',
    },
  ];

  return (
    <Layout>
      {/* Hero — full-width billboard (image + navy overlay + left text) */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-fs-010-about_history-v2.webp"
          alt="Materials and furnishings that tell the association's history and the lineage of butler culture since its founding"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Our History — Since 1890</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">A Lineage Handed Down</span>
              <span className="block">Across 135 Years</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              Since its founding in 1890 (Meiji 23), the association has walked a long road in the development of butler culture in Japan. Fusing the Western tradition with the Japanese spirit of hospitality, we tell the 135-year story handed down through three Presidents.
            </p>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* Body = side nav + content (2 columns) */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-14">

            {/* min-w-0 wrapper: suppress SP horizontal scroll (grid item min-width:auto) by confining
                the sidenav's horizontal pill row to internal scroll (AboutSideNav itself unedited). */}
            <div className="min-w-0">
              <AboutSideNav items={aboutNav} activeHref="/en/about/history" eyebrow="About Us" heading="About the Association" />
            </div>

            <div className="min-w-0 space-y-16 sm:space-y-20 lg:space-y-24">

              {/* I. Prologue — lead + editorial data row */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Prologue</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  What Has Been <span className="text-[#a38e65]">Handed Down</span>
                </h2>
                <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                  <p>
                    The butler took root in Japan 135 years ago. The association's history begins in the modernization of the Meiji era, continues through postwar nationwide growth, and passes through division and reunification to the present. It is the journey of a single ideal, handed down while changing shape with each age.
                  </p>
                  <p>
                    What changed, and what did not — the methods of training and the very form of daily life have shifted with the times. Yet the founding resolve, to bind Western discipline and dignity together with Japanese etiquette, has been kept without a single break under three Presidents.
                  </p>
                  <p>
                    On this page we trace the journey from the founding years to the present, chaptered by the eras of Meiji, Showa, Heisei, and Reiwa. Alongside the events of each year, we tell what the association held dear at that moment.
                  </p>
                </div>
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Founded</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">1890</dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Heritage</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">135<span className="text-sm text-[#8f7a56] ml-1">yrs</span></dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Presidents</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">3</dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Reunified</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">2000</dd>
                  </div>
                </dl>
              </section>

              {/* II. Chronicle — full-version era-chaptered timeline (main feature).
                   New variation: developed from the EN overview's simple vertical digest into an
                   era-chaptered full chronicle with Roman-numeral era badges, thick narrative, and
                   year-emphasized rows (28–32px). */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Chronicle</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-4">
                  A Journey in <span className="text-[#a38e65]">Four Chapters</span>
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-12 sm:mb-14">
                  We trace the association's 135 years across four eras — the founding years of Meiji, the growth of Showa, the reunion and renewal of Heisei, and the succession of Reiwa. Each chapter recounts what the association held dear in that age, followed by a timeline that revisits the milestones one by one.
                </p>

                <div className="space-y-16 sm:space-y-20">
                  {eras.map((era) => (
                    <article key={era.badge}>
                      {/* Chapter header: navy Roman-numeral badge + era name + years */}
                      <div className="flex items-center gap-5 mb-7">
                        <span className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#1a2942] border border-[#a38e65]/50 flex items-center justify-center font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-2xl tracking-[0.04em]">
                          {era.badge}
                        </span>
                        <div>
                          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                            <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl leading-tight">{era.name}</h3>
                            <span className="font-['Playfair_Display',serif] text-[#a38e65] text-base sm:text-lg tracking-[0.08em]">{era.range}</span>
                          </div>
                        </div>
                      </div>

                      {/* Chapter narrative + image (avoids consecutive image-less sections; fixed-height wide band, not ultra-wide) */}
                      <div className="grid lg:grid-cols-[1fr_340px] gap-8 lg:gap-12 items-start mb-9">
                        <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                          {era.narrative.map((para, i) => (
                            <p key={i}>{para}</p>
                          ))}
                        </div>
                        <div className="lg:pt-1">
                          <img
                            src={era.image}
                            alt={era.imageAlt}
                            className="w-full h-52 lg:h-[240px] object-cover border border-[#a38e65]/30"
                          />
                        </div>
                      </div>

                      {/* Timeline rows: year emphasized (28–32px serif) + event + description, brass vertical rule */}
                      <div className="border-l border-[#e2ddd2] pl-6 sm:pl-8 space-y-7">
                        {era.rows.map((row) => (
                          <div key={row.year} className="relative">
                            <span className="absolute -left-[26px] sm:-left-[34px] top-2 w-[11px] h-[11px] rounded-full bg-[#a38e65] ring-4 ring-white"></span>
                            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-6">
                              <span className="font-['Playfair_Display',serif] text-[#a38e65] text-[28px] sm:text-[32px] leading-none tracking-[0.02em] mb-2 sm:mb-0 sm:w-[92px] sm:shrink-0">{row.year}</span>
                              <div className="min-w-0">
                                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2 leading-[1.5]">{row.label}</h4>
                                <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#4a4638] leading-[1.9] max-w-2xl">{row.text}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </article>
                  ))}
                </div>
              </section>

              {/* III. Lineage — the three Presidents (new variation, distinct from the timeline) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Lineage</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  A Succession of <span className="text-[#a38e65]">Three Presidents</span>
                </h2>
                <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-12 items-start mb-10">
                  <div className="lg:pt-1 order-last lg:order-first">
                    <img
                      src="/images/generated/jba-fs-011-about_history-v2.webp"
                      alt="The bearing of the butler handed down within the association, its craft and heart inherited across generations"
                      className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30"
                    />
                  </div>
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                    The association's 135 years have been handed down by three Presidents. The ideal raised by founder Shingo Tanaka was brought to fruition as a unified nationwide structure by second President Kazuhiko Yamada, and is bridged into a new era by third President Yuichi Ishii. Generations change, yet the spirit of the butler has been passed on without a single break.
                  </p>
                </div>
                <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                  {presidents.map((p) => (
                    <div key={p.name} className="border-t-2 border-[#1a2942] bg-[#f8f6f0] p-7 lg:p-8">
                      <span className="inline-block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.08em] mb-3">{p.gen}</span>
                      <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-[26px] leading-tight mb-2">{p.name}</h3>
                      <p className="font-['Playfair_Display',serif] text-[#a38e65] text-lg tracking-[0.04em] mb-4">{p.period}</p>
                      <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{p.description}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* IV. The Path Ahead — navy CTA panel */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. The Path Ahead</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  The Association Ahead
                </h2>
                <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                  <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                    On the foundation of 135 years of history, the association will continue to devote itself to the development of butler culture in Japan. Through cultivating the talent of the next generation and pursuing service attuned to the changes of daily life, we bridge what we have inherited toward the next hundred years. If you are interested in our certifications and services, please explore further.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <a href="/en/qualifications" className={BTN08_ON_NAVY}>Certifications</a>
                    <a href="/en/butler-services/" className={BTN08_ON_NAVY}>Butler Services</a>
                    <a href="/en/contact" className={BTN08_ON_NAVY}>Contact Us</a>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
